import { ArtistJobs, FlightPaymentType } from '@customTypes/types';
import {
  Flex,
  RadioGroup,
  ScrollAreaAutosize,
  Space,
  Stack,
  Text,
  Title
} from '@mantine/core';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import { useCreateWorkItemForm } from '@/features/work/components/create-work-item-form/useCreateWorkItemForm';
import { translate } from '@/shared/i18n/translate';
import { LBButton, LBDatePicker, LBRadio } from '@/shared/ui-kit/components';

export const CreateWorkItemForm = ({
  artistProfileId,
  clubProfileId,
  onSubmittingSuccess,
  workItemType,
  onCancel
}: {
  artistProfileId: number;
  clubProfileId: number;
  workItemType: 'request' | 'invite';
  onSubmittingSuccess?: () => void;
  onCancel: () => void;
}) => {
  const { formatMessage } = useIntl();
  const {
    state: { form, clubProfile },
    functions: { onSubmit }
  } = useCreateWorkItemForm({
    artistProfileId,
    clubProfileId,
    onSubmittingSuccess,
    workItemType
  });

  return (
    <form onSubmit={onSubmit}>
      <ScrollAreaAutosize>
        <Title order={2}>{translate('popups.createWorkItem.title')}</Title>
        <Stack h={8} />
        {workItemType === 'invite' && (
          <Text fz={14}>
            {translate('popups.createWorkItem.inviteDescription')}
          </Text>
        )}
        {workItemType === 'request' && (
          <Text fz={14}>
            {translate('popups.createWorkItem.requestDescription')}
          </Text>
        )}
        <Space h={24} />
        <Stack gap={16}>
          <LBDatePicker
            label={translate('form.approximateStartDate.label')}
            maw={150}
            minDate={dayjs().add(3, 'day').toDate()}
            placeholder={formatMessage({ id: 'commons.dateFormatPlaceholder' })}
            {...form.getInputProps('startDate')}
            key={form.key('startDate')}
          />
          <LBDatePicker
            disabled={!form.getValues().startDate}
            label={translate('form.approximateEndDate.label')}
            maw={150}
            placeholder={formatMessage({ id: 'commons.dateFormatPlaceholder' })}
            minDate={
              form.getValues().startDate
                ? dayjs(form.getValues().startDate)
                    .add(
                      clubProfile?.contractTerms.minContractDurationWeeks ?? 0,
                      'week'
                    )
                    .toDate()
                : undefined
            }
            {...form.getInputProps('endDate')}
            key={form.key('endDate')}
          />
          <Flex direction='column' gap={8}>
            {workItemType === 'invite' && (
              <Text fz={14}>
                {translate('popups.createWorkItem.suggestVacancy')}
              </Text>
            )}
            {workItemType === 'request' && (
              <Text fz={14}>
                {translate('popups.createWorkItem.selectVacancy')}
              </Text>
            )}
            <RadioGroup
              {...form.getInputProps('jobRole')}
              key={form.key('jobRole')}
            >
              <Stack gap={8}>
                <LBRadio
                  value={ArtistJobs.Strip}
                  label={formatMessage({
                    id: `commons.artistJobs.${ArtistJobs.Strip}`
                  })}
                />
                <LBRadio
                  value={ArtistJobs.GoGo}
                  label={formatMessage({
                    id: `commons.artistJobs.${ArtistJobs.GoGo}`
                  })}
                />
                <LBRadio
                  value={ArtistJobs.Hostess}
                  label={formatMessage({
                    id: `commons.artistJobs.${ArtistJobs.Hostess}`
                  })}
                />
              </Stack>
            </RadioGroup>
          </Flex>
          <Flex direction='column' gap={8}>
            {workItemType === 'invite' && (
              <Text fz={14}>
                {translate('popups.createWorkItem.ticketCreditOption')}
              </Text>
            )}
            {workItemType === 'request' && (
              <Text fz={14}>
                {translate('popups.createWorkItem.ticketCreditRequired')}
              </Text>
            )}

            <RadioGroup
              value={JSON.stringify(form.getValues().ticketCreditation)}
              onChange={(value) => {
                form.setFieldValue(
                  'ticketCreditation',
                  JSON.parse(value) as boolean
                );
              }}
            >
              <Stack gap={8}>
                <LBRadio
                  label={translate('commons.yes')}
                  value='true'
                  disabled={
                    clubProfile?.contractTerms.flightPaymentType ===
                    FlightPaymentType.Credit
                  }
                />
                <LBRadio
                  label={translate('commons.no')}
                  value='false'
                  disabled={
                    clubProfile?.contractTerms.flightPaymentType ===
                    FlightPaymentType.Credit
                  }
                />
              </Stack>
            </RadioGroup>
          </Flex>
        </Stack>
        <Space h={32} />
        <Flex direction='column' gap={15}>
          {' '}
          <LBButton type='submit'> {translate('commons.confirm')}</LBButton>
          <LBButton variant='outline' onClick={onCancel}>
            {translate('commons.cancel')}
          </LBButton>
        </Flex>
      </ScrollAreaAutosize>
    </form>
  );
};
